import {
  Building,
  Confetti,
  User,
  Users,
  UsersThree,
} from '@phosphor-icons/react';
import { useDebounceEffect } from 'ahooks';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { PricingCard } from 'src/components/PricingCard';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import useOrganization from 'src/hooks/useOrganization';
import { useOrganizationSwitcher } from 'src/hooks/useOrganizationSwitcher';
import Stripe from 'stripe';
import { SpendingControls } from './components/SpendingControls';

const planImgs: { [key: string]: ReactNode } = {
  developer: <User size={24} />,
  team: <Users size={24} />,
  business: <UsersThree size={24} />,
};

const FREE_PLAN_NAME = 'Trial';

const CUSTOM_PLAN_FEATURES = [
  '15000+ minutes',
  'Overage: >= $0.06/min',
  '50+ numbers',
  '25+ concurrent calls',
];

type Plan = {
  id: string;
  billingPriceId: string;
  price: string;
  name: string;
  features: string[];
  meteredPriceId: string;
};

export const Billing = () => {
  const { get, patch, post } = useAuthedRequest();
  const notification = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organization } = useOrganization();
  const {
    currentOrg,
    orgsLoading,
    getOrganizationOptions,
    handleChangeOrganization,
  } = useOrganizationSwitcher({ returnTo: '/billing' });

  const key = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  if (!key) {
    throw 'No Stripe key configured. Check environment variables.';
  }

  const [currentSubscription, setCurrentSubscription] =
    useState<Stripe.Subscription>();
  const [stripePlan, setStripePlan] = useState<Stripe.Plan>();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan>();
  const [loading, setLoading] = useState(true);
  const [includedMinutes, setIncludedMinutes] = useState<number>();
  const [overageRate, setOverageRate] = useState<number>();

  const redirectCheckout = async (
    planPriceOption: string,
    meteredPriceOption: string,
  ) => {
    const results = await post('/stripe/checkout-session', {
      planPriceOption,
      meteredPriceOption,
    });
    window.location.href = results;
  };

  const fetchCurrentPlan = async () => {
    if (!organization.id) {
      return;
    }

    try {
      const org = await get(`/organizations/${organization.id}`);
      if (!org.planId) {
        return;
      }

      setCurrentPlan(plans.find((plan) => plan.id === org.planId));
    } catch (error) {
      console.error('Error fetching current plan', error);
    }
  };

  const redirectBillingPortalUrl = async () => {
    const results = await post('/stripe/customer-portal-session', {});
    window.location.href = results;
  };

  const updateSubscription = async (
    planPriceId: string,
    meteredPriceId: string,
  ) => {
    await patch('/stripe/subscription', {
      planPriceOption: planPriceId,
      meteredPriceOption: meteredPriceId,
    });
    await fetchSubscriptions();
  };

  const fetchSubscriptions = async () => {
    const results = await get('/stripe/subscription');
    if (!results.length) {
      return;
    }
    setCurrentSubscription(results[0]);
  };

  const fetchBillingPlans = async () => {
    const plans: Plan[] = await get('/plans');
    setPlans(plans);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchBillingPlans();
      await fetchSubscriptions();
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (plans && plans.length && organization) {
      fetchCurrentPlan();
    }
  }, [organization, plans]);

  useEffect(() => {
    const tieredPriceId = currentSubscription?.items.data.filter(
      (item) => item.price.billing_scheme === 'tiered',
    )[0]?.price.id;
    if (tieredPriceId) {
      const fetchPriceData = async () => {
        const price: Stripe.Price = await get(`/stripe/price/${tieredPriceId}`);
        if (!price.tiers) {
          return;
        }
        setIncludedMinutes(
          (price.tiers.find((tier) => tier.up_to)?.up_to ?? 0) / 60,
        );
        const tierRate = price.tiers
          .map((tier) => parseFloat(tier.unit_amount_decimal ?? '0'))
          .find((rate) => rate > 0);
        if (tierRate) {
          setOverageRate((tierRate * 60) / 100);
        }
      };
      fetchPriceData();
    }
    setStripePlan(currentSubscription?.items.data[0].plan);
  }, [currentSubscription]);

  const currentPlanIndex = plans.findIndex(
    (plan) => plan.billingPriceId === stripePlan?.id,
  );

  useDebounceEffect(() => {
    const errorType = searchParams.get('error');
    if (!errorType) {
      return;
    }

    let message = '';
    if (errorType === 'LimitError') {
      message =
        "You've reached the limit of your plan. Please upgrade to use more.";
    } else if (errorType === 'plan-expired') {
      message =
        'Your plan has expired. Please upgrade your plan to continue using the product.';
    }
    if (message) {
      notification.addNotification(message, {
        position: 'top-center',
        type: 'warning',
        persist: 10000,
      });

      navigate('/billing');
    }
  }, [searchParams]);

  return (
    <div className="flex-1">
      <Heading
        title={
          <>
            <h1>
              Billing
              {currentOrg && <> for</>}
            </h1>

            <AutoCompleteSelectBox
              key={String(currentOrg?.value) + '-' + String(currentOrg?.label)}
              defaultValue={currentOrg}
              fetchOptions={getOrganizationOptions}
              onChange={(value) => handleChangeOrganization(value)}
              className="text-xl font-bold"
              variant="text"
              size="medium"
              disabled={orgsLoading}
              defaultOptions={[]}
              allowRemoveOption={false}
            />
          </>
        }
        subtitle="Choose the option that fits your needs today"
      />
      {loading && <Loading className="mt-20" />}
      {!loading && (
        <ContentWrapper>
          {currentSubscription && (
            <div className="flex items-center">
              <div>
                Current subscription:{' '}
                <span className="font-bold">
                  {plans.find((plan) => plan.billingPriceId === stripePlan?.id)
                    ?.name ||
                    currentPlan?.name ||
                    'Custom'}
                </span>
                {currentSubscription.cancel_at && (
                  <span className="font-bold">
                    {` ends on ${new Date(currentSubscription.cancel_at * 1000).toLocaleDateString()}`}
                  </span>
                )}
              </div>
              <Button className={'ml-4'} onClick={redirectBillingPortalUrl}>
                Manage existing subscription
              </Button>
            </div>
          )}
          {!!includedMinutes && !!overageRate && (
            <div className="flex flex-col">
              <hr className="my-4" />
              <SpendingControls
                includedMinutes={includedMinutes}
                overageRate={parseFloat(overageRate.toFixed(2))}
              />
              <hr className="my-4" />
            </div>
          )}
          {!currentSubscription && (
            <div className="flex items-center">
              <div>
                Current subscription:{' '}
                <span className="font-bold">
                  {currentPlan?.name || FREE_PLAN_NAME}
                </span>
              </div>
            </div>
          )}
          <div className="flex w-full">
            <div
              className={`max-w-[1240px] mx-auto grid lg:grid-cols-4 gap-20 align-center`}
            >
              {plans.map((plan, index) => {
                if (
                  plan.billingPriceId &&
                  !plan.billingPriceId.startsWith('price')
                ) {
                  // Not a Stripe plan
                  return;
                }
                let pricingLabel = '';
                if (currentPlanIndex > -1 && currentPlanIndex < index) {
                  pricingLabel = 'Upgrade plan';
                } else if (currentPlanIndex === index) {
                  pricingLabel = 'Current plan';
                }
                return (
                  <PricingCard
                    key={plan.billingPriceId}
                    onClick={async () => {
                      if (!pricingLabel) {
                        await redirectCheckout(
                          plan.billingPriceId,
                          plan.meteredPriceId,
                        );
                      }
                      await updateSubscription(
                        plan.billingPriceId,
                        plan.meteredPriceId,
                      );
                    }}
                    disabled={
                      !plan.billingPriceId ||
                      stripePlan?.id === plan.billingPriceId ||
                      (!pricingLabel && !!stripePlan?.id)
                    }
                    buttonLabel={pricingLabel || ''}
                    title={plan.name}
                    price={
                      isNaN(parseInt(plan.price))
                        ? 'Free'
                        : `$${parseInt(plan.price) / 100}`
                    }
                    img={
                      planImgs[plan.name.toLowerCase()] || (
                        <Confetti size={24} />
                      )
                    }
                    features={plan.features}
                  />
                );
              })}
              <PricingCard
                key={'custom'}
                onClick={() => {
                  window.location.href =
                    'mailto:support@fluents.ai?subject=Enterprise+Inquiry';
                }}
                buttonLabel={'Contact us'}
                title="Enterprise"
                price="$1499+"
                features={CUSTOM_PLAN_FEATURES}
                img={<Building size={24} />}
              />
            </div>
          </div>
        </ContentWrapper>
      )}
    </div>
  );
};
